import { loan_application, Product } from '@axo/shared/data-access/types';
import { ApplicationDataDateField } from '../../Fields/ApplicationDataDateField';
import { ApplicationDataSelectField } from '../../Fields/ApplicationDataSelectField';
import { ApplicantDataApplicantTabPanelSection } from '../ApplicationDataApplicantTabPanelLayout';
import { MarketCountryCode } from '@axo/shared/data-access/types/ace_index';
import { Broker } from '@axo/shared/data-access/types/broker';
import { ApplicationConditionalWrapper } from '../../../ConditionalWrappers/ApplicationConditionalWrapper';
import { useCitizenshipOptions } from './options/useCitizenshipOptions';
import { useResidencePermitOptions } from './options/useResidencePermitOptions';
import { useResidencePermitTypeOptions } from './options/useResidencePermitTypeOptions';
import { useFormContext } from 'react-hook-form';
import { ApplicationDataFormValues } from '../../ApplicationDataForm/ApplicationDataFormSchema';
import { ApplicationDataTextField } from '../../Fields/ApplicationDataTextField';

interface PersonalSectionProps {
  applicationID: string;
  coApplicantCustomerID: string | undefined;
  coApplicantPersonID: string | undefined;
}

export const PersonalSection: React.FC<PersonalSectionProps> = ({
  applicationID,
  coApplicantCustomerID,
  coApplicantPersonID,
}) => {
  const { setValue } = useFormContext<ApplicationDataFormValues>();

  const citizenshipOptions = useCitizenshipOptions();

  const residencePermitOptions = useResidencePermitOptions();
  const residencePermitTypeOptions = useResidencePermitTypeOptions(
    coApplicantCustomerID,
  );

  const isCoApplicant = coApplicantCustomerID || coApplicantPersonID;

  return (
    <ApplicantDataApplicantTabPanelSection title="Personal">
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [
                MarketCountryCode.Finland,
                MarketCountryCode.Norway,
                MarketCountryCode.Denmark,
              ],
              productType: [Product.UnsecuredLoan],
            },
            {
              productType: [Product.SecuredLoan],
              marketCountry: [MarketCountryCode.Norway],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="Citizenship"
            label="Citizenship"
            options={citizenshipOptions}
          />
        </ApplicationConditionalWrapper>
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [
                MarketCountryCode.Norway,
                MarketCountryCode.Denmark,
              ],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="CoApplicantCitizenship"
            label="Citizenship"
            options={citizenshipOptions}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [
                MarketCountryCode.Norway,
                MarketCountryCode.Finland,
              ],
              productType: [Product.UnsecuredLoan],
            },
            {
              productType: [Product.SecuredLoan],
              marketCountry: [MarketCountryCode.Norway],
            },
          ]}
        >
          <ApplicationDataDateField
            applicationID={applicationID}
            name="LivedInMarketCountrySince"
            label="Lived in country since"
          />
        </ApplicationConditionalWrapper>
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Norway],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataDateField
            applicationID={applicationID}
            name="CoApplicantLivedInMarketCountrySince"
            label="Lived in country since"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.UnsecuredLoan],
              marketCountry: [
                MarketCountryCode.Norway,
                MarketCountryCode.Finland,
              ],
            },
            {
              productType: [Product.SecuredLoan],
              marketCountry: [MarketCountryCode.Norway],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="Education"
            label="Education"
            options={[
              { value: null, label: '-' },
              ...Object.values(loan_application.Education).map((value) => ({
                value,
              })),
            ]}
          />
        </ApplicationConditionalWrapper>
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Norway],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="CoApplicantEducation"
            label="Education"
            options={[
              { value: null, label: '-' },
              ...Object.values(loan_application.Education).map((value) => ({
                value,
              })),
            ]}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Finland],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="MilitaryServiceCompleted"
            label="Military Service Completed"
            options={[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ]}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Finland],
              productType: [Product.UnsecuredLoan],
              broker: [Broker.Zmarta],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="PoliticallyInfluential"
            label="Politically influential"
            options={[
              { value: true, label: 'Yes' },
              { value: false, label: '-' },
            ]}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="ResidencePermit"
            label="Residence permit"
            options={residencePermitOptions}
            onChangeSideEffect={() => {
              setValue('ResidencePermitType', null);
            }}
          />
        </ApplicationConditionalWrapper>
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="CoApplicantResidencePermit"
            label="Residence permit"
            options={residencePermitOptions}
            onChangeSideEffect={() => {
              setValue('CoApplicantResidencePermitType', null);
            }}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="ResidencePermitType"
            label="Residence permit type"
            options={residencePermitTypeOptions}
          />
        </ApplicationConditionalWrapper>
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="CoApplicantResidencePermitType"
            label="Residence permit type"
            options={residencePermitTypeOptions}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataTextField
            applicationID={applicationID}
            name="ResidencePermitNumber"
            label="Residence permit number"
          />
        </ApplicationConditionalWrapper>
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataTextField
            applicationID={applicationID}
            name="CoApplicantResidencePermitNumber"
            label="Residence permit number"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataDateField
            applicationID={applicationID}
            name="ResidencePermitExpiration"
            label="Residence permit expiration"
          />
        </ApplicationConditionalWrapper>
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataDateField
            applicationID={applicationID}
            name="CoApplicantResidencePermitExpiration"
            label="Residence permit expiration"
          />
        </ApplicationConditionalWrapper>
      )}
    </ApplicantDataApplicantTabPanelSection>
  );
};
